interface SystemFeaturesSectionProps {
  onCTAClick?: () => void;
}

const features = [
  {
    icon: 'ri-store-2-line',
    title: '免登入線上高質感展間',
    description: '客戶無需下載 APP 或註冊帳號，直接透過連結瀏覽高質感線上展示空間，第一印象即建立品牌信任。',
    color: 'from-[#00D4FF] to-[#0099CC]',
    border: 'border-[#00D4FF]/30',
    accent: 'text-[#00D4FF]',
  },
  {
    icon: 'ri-user-settings-line',
    title: '企業內部權限登入',
    description: '每位員工擁有獨立後台帳號（User Profiles），資料分層管理，杜絕越權操作與業績歸屬爭議。',
    color: 'from-[#D4AF37] to-[#F0C040]',
    border: 'border-[#D4AF37]/30',
    accent: 'text-[#D4AF37]',
  },
  {
    icon: 'ri-shield-check-line',
    title: '防呆業績回報模組',
    description: '每筆成交必須完整填寫結構化表單才能入帳，從源頭杜絕漏報、誤報與帳目糾紛，管理不再靠口頭約定。',
    color: 'from-[#00D4FF] to-[#0099CC]',
    border: 'border-[#00D4FF]/30',
    accent: 'text-[#00D4FF]',
  },
  {
    icon: 'ri-database-2-line',
    title: '追蹤代碼 (GTM/Pixel) 埋設工程',
    description: '完整佈建 Google Tag Manager 與 Meta Pixel 追蹤架構，讓每一筆廣告點擊都可追溯至最終成交行為。',
    color: 'from-[#D4AF37] to-[#F0C040]',
    border: 'border-[#D4AF37]/30',
    accent: 'text-[#D4AF37]',
  },
  {
    icon: 'ri-lock-password-line',
    title: '數據安全防護工程',
    description: '系統升級為企業付費版，徹底移除底層平台浮水印，並綁定專屬品牌網址。對外看就是您專屬的頂級雲端 APP。',
    color: 'from-[#00D4FF] to-[#0099CC]',
    border: 'border-[#00D4FF]/30',
    accent: 'text-[#00D4FF]',
  },
];

export default function SystemFeaturesSection({ onCTAClick }: SystemFeaturesSectionProps) {
  return (
    <section className="py-20 sm:py-28 bg-[#0A192F] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#D4AF37]/5 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-[#00D4FF]/5 rounded-full blur-[100px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* 標題區 */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-5 py-2 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-widest mb-6">
            <i className="ri-settings-3-line"></i>
            <span>NT$ 48,000 數位引擎模組建置包含</span>
          </div>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            企業級 O2O 模組
            <span className="block mt-1 bg-gradient-to-r from-[#D4AF37] to-[#F0C040] bg-clip-text text-transparent">
              五大核心功能架構
            </span>
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            一次架設，長期運作。讓您的企業從此擺脫人工管理混亂，
            迎向<strong className="text-white">數據化、防呆化、自動化</strong>的數位新時代。
          </p>
        </div>

        {/* 功能卡片網格 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-12">
          {features.slice(0, 3).map((feature, index) => (
            <FeatureCard key={index} feature={feature} />
          ))}
        </div>
        {/* 中段 CTA 插入 */}
        {onCTAClick && (
          <div className="max-w-3xl mx-auto mb-6">
            <div
              className="relative rounded-2xl overflow-hidden px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4"
              style={{
                background: 'linear-gradient(135deg, #00D4FF08, #D4AF3708)',
                border: '1px solid #00D4FF30',
              }}
            >
              {/* 左側裝飾線 */}
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#00D4FF] to-[#D4AF37] rounded-l-2xl" />
              <div className="flex items-center gap-3 pl-2">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#00D4FF]/15 flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-[#00D4FF] text-lg"></i>
                </div>
                <p className="text-gray-300 text-sm leading-snug">
                  <span className="text-white font-semibold">這五大模組</span>，您的企業目前具備幾項？
                  <span className="text-[#D4AF37] font-medium ml-1">立即診斷缺口，搶先佈局。</span>
                </p>
              </div>
              <button
                onClick={onCTAClick}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer transition-all duration-300 hover:scale-105 flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                  color: '#060F1E',
                  boxShadow: '0 4px 16px #00D4FF30',
                }}
              >
                <i className="ri-search-eye-line"></i>
                免費獲客健檢
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 max-w-3xl mx-auto mb-14">
          {features.slice(3).map((feature, index) => (
            <FeatureCard key={index + 3} feature={feature} />
          ))}
        </div>

        {/* 底部 CTA 橫幅 */}
        <div className="bg-gradient-to-r from-[#0F2744] to-[#1A1200] rounded-2xl border border-[#D4AF37]/20 p-6 sm:p-10 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-center lg:text-left">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#F0C040] rounded-xl flex-shrink-0">
              <i className="ri-building-4-line text-2xl text-[#060F1E]"></i>
            </div>
            <div>
              <h4 className="text-lg sm:text-xl font-bold text-white mb-1">企業級 SaaS 敏捷導入 (Agile SaaS Implementation)</h4>
              <p className="text-gray-400 text-sm max-w-lg leading-relaxed">
                每個企業的管理邏輯不同，打造您的專屬品牌雲端工作站，確保系統完全符合您的業務流程，快速成為您的印鈔工具。
              </p>
            </div>
          </div>
          {onCTAClick && (
            <button
              onClick={onCTAClick}
              className="w-full lg:w-auto bg-gradient-to-r from-[#D4AF37] to-[#F0C040] text-[#060F1E] px-8 py-3.5 rounded-full font-bold text-sm hover:shadow-[0_4px_20px_rgba(212,175,55,0.5)] transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 flex-shrink-0"
            >
              <i className="ri-calendar-check-line"></i>
              預約免費數位診斷
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

interface FeatureCardProps {
  feature: typeof features[0];
}

function FeatureCard({ feature }: FeatureCardProps) {
  return (
    <div className={`bg-[#0F2744] border ${feature.border} rounded-2xl p-6 sm:p-7 hover:shadow-lg hover:shadow-[#00D4FF]/10 transition-all duration-300 group`}>
      <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br ${feature.color} rounded-xl mb-5 group-hover:scale-110 transition-transform`}>
        <i className={`${feature.icon} text-2xl text-white`}></i>
      </div>
      <h3 className={`text-lg font-bold mb-3 ${feature.accent}`}>{feature.title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
    </div>
  );
}
